import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';

/**
 * Guards pages that need a logged in user. Visitors are sent to sign-up (purchases require
 * an account), keeping the page they wanted as ?next= so they come back after registering.
 */
export default function RequireAuth({ children, to = '/register' }: { children: ReactNode; to?: '/register' | '/login' }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return <section className="section page-section"><p className="section-desc">Loading…</p></section>;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`${to}?next=${next}`} replace />;
  }
  return <>{children}</>;
}

/** Guards admin pages: visitors go to login, logged-in non-admins see a not-found page. */
export function RequireAdmin({ children }: { children: ReactNode }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) return <section className="section page-section"><p className="section-desc">Loading…</p></section>;
    if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
    if (user.role !== 'admin') return <Navigate to="/404" replace />;
    return <>{children}</>;
}

/** Only allows same-site relative paths as redirect targets. */
export function safeNext(next: string | null | undefined, fallback = '/account'): string {
  return next && /^\/(?!\/)/.test(next) ? next : fallback;
}

/** The CV generator is personal to this account (mirrors CV_OWNER_EMAIL in backend/forgetit/resume-helper/cv.ts). */
const CV_OWNER_EMAIL = 'otterstroems@gmail.com';

export const isCvOwner = (user: { email: string } | null) => user?.email.toLowerCase() === CV_OWNER_EMAIL;

/** Guards the CV generator: visitors go to login, everyone but the owner sees a not-found page. */
export function RequireCvOwner({ children }: { children: ReactNode }) {
    const { user, loading } = useAuth();
    const location = useLocation();
    if (loading) return <section className="section page-section"><p className="section-desc">Loading…</p></section>;
    if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
    if (!isCvOwner(user)) return <Navigate to="/404" replace />;
    return <>{children}</>;
}
